import { useMemo } from 'react';
import { InlineNotification } from '@carbon/react';
import type { SortingState } from '@tanstack/react-table';
import { DataTable } from 'components/data-table';
import { decisionFiltersAtom, EMPTY_FILTERS } from './atoms';
import { useDecisions } from './hooks';
import { DECISION_COLUMNS } from './decision-columns';
import { DecisionDetail } from './decision-detail';
import { DecisionFilters } from './decision-filters';
import { DecisionStats } from './decision-stats';
import { useLogFilters } from '../hooks';
import styles from './decision-log.module.scss';

/**
 * The auto-match decision log: stats, filters, and the server-paged, sorted
 * table of decisions, each row expanding to its full detail.
 * @return {JSX.Element} The log
 */
export function DecisionLog() {
  const { filters, patchFilter, onPaginationChange } = useLogFilters(
    decisionFiltersAtom,
    EMPTY_FILTERS
  );
  const { data: summary, isFetching, isError } = useDecisions(filters);
  const rows = useMemo(() => summary?.results ?? [], [summary]);

  /**
   * Writes the table's sort intent to the filters, back on page 1. The
   * DataTable disables sort removal in server-driven mode, so a click always
   * yields a sorted column (a third click just flips the direction).
   * @param {SortingState} sorting - The table's new sort
   */
  const handleSortChange = (sorting: SortingState) => {
    const sort = sorting[0];
    if (!sort) return;
    patchFilter({ sortBy: sort.id, sortDir: sort.desc ? 'desc' : 'asc' });
  };

  return (
    <div className={styles.container}>
      {summary && (
        <DecisionStats
          stats={summary.stats}
          invariants={summary.invariants}
          reversalRate={summary.reversalRate}
        />
      )}
      <DecisionFilters />
      {isError && (
        <InlineNotification
          kind="error"
          lowContrast
          hideCloseButton
          title="Failed to load decisions"
          subtitle="Refresh to try again."
        />
      )}

      <DataTable
        data={rows}
        columns={DECISION_COLUMNS}
        getRowId={(row) => String(row.id)}
        isLoading={isFetching}
        persistenceKey="auto-match-decision-log"
        // Server-driven, and the filters atom is the single source of truth for
        // sort + page — pass them as controlled slices so the footer page and the
        // sort caret follow an external filter change or Reset, not just changes
        // made through the table itself.
        controlledState={{
          pagination: { page: filters.page, pageSize: filters.perPage },
          sorting: [{ id: filters.sortBy, desc: filters.sortDir === 'desc' }],
        }}
        scroll={{
          mode: 'paginated',
          serverDriven: true,
          totalCount: summary?.totalCount ?? 0,
        }}
        onSortChange={(s) => handleSortChange(s.sorting)}
        onPaginationChange={onPaginationChange}
        renderRowSubComponent={(row) => (
          <DecisionDetail decisionId={row.original.id} />
        )}
        config={{
          columnResizing: true,
          columnReordering: false,
          columnPinning: false,
          sorting: true,
          // Server-driven: the list must always be sorted, so a third click cycles
          // the direction rather than clearing to an unsorted (backend-ordered) state.
          sortingRemoval: false,
          columnVisibility: false,
          globalSearch: false,
          columnFilters: false,
        }}
      />
    </div>
  );
}
